import { t as uiText } from '../i18n';
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceAccessQuery } from '../features/auth/queries/workspaceAccessQueries';

type AccessControlProps = {
  workspaceId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode; // 任意：ローディング時の置き換え
};

const AccessControl: React.FC<AccessControlProps> = ({ workspaceId, children, fallback }) => {
  const { user, initializing, stabilizing } = useAuth();
  const { data: hasAccess, isLoading: accessLoading } = useWorkspaceAccessQuery(
    workspaceId,
    !initializing && !stabilizing // 初期化中/認証揺れ中はアクセスチェックを走らせない
  );

  // /home の既定遷移用に「最後に開いたワークスペース」を保存（別タブ干渉は許容範囲＝入口用途のみ）
  React.useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage?.setItem('lastWorkspaceId', workspaceId);
    } catch {
      // localStorage が無効（制限/プライベートモード等）でもアクセス制御自体は継続
    }
  }, [workspaceId]);

  // アクセス制御の状態変化をログ出力（最適化: 重要な状態変化のみ）
  React.useEffect(() => {
    // ローディング中は詳細ログを出力しない
    if (initializing || accessLoading) return;
  }, [workspaceId, user?.uid, hasAccess]); // initializing, accessLoading を依存配列から除外

  if (initializing || stabilizing || accessLoading) {
    return <>{fallback ?? (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid var(--workspace-primary, #cfae91)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{
            fontSize: '16px',
            color: '#666'
          }}>{stabilizing ? uiText("Checking sign-in...") : uiText("Checking access...")}</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )}</>;
  }

  if (!user || !hasAccess) {
    return <Navigate to="/sys/access-denied" replace />;
  }

  return <>{children}</>;
};

export default AccessControl; 